import { Cloud, Shield, MessageSquare, GitBranch, Database } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IntegrationsProps {
  data: {
    title: string;
    subtitle: string;
    categories: Array<{
      title: string;
      description: string;
    }>;
    footer: string;
  };
  className?: string;
}

const iconMap = {
  'ServiceNow': Database,
  'Azure AD': Shield,
  'AWS': Cloud,
  'Slack & Teams': MessageSquare,
  'Terraform + GitHub': GitBranch,
};

/**
 * Integrations Section Component
 * Shows platform integrations
 */
export function Integrations({ data, className }: IntegrationsProps) {
  return (
    <section id="integrations" className={cn("py-20 bg-gradient-subtle", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">
            {data.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {data.subtitle}
          </p>
        </div>

        {/* Integrations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
          {data.categories.map((integration, index) => {
            const IconComponent = iconMap[integration.title as keyof typeof iconMap] || Cloud;
            
            return (
              <div key={index} className="gradient-card rounded-xl p-6 shadow-card hover:shadow-lg transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="w-8 h-8 text-primary" />
                </div>
                
                <h3 className="text-lg font-semibold mb-2">{integration.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {integration.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Footer Message */}
        <div className="text-center">
          <div className="gradient-card rounded-2xl p-8 shadow-card max-w-2xl mx-auto">
            <p className="text-lg font-medium text-primary">
              {data.footer}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
